import { Button, Input, Modal } from '@sjuni/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import { useAuth } from '@/lib/auth/useAuth';

import { FormError } from './FormError';
import { SubmitButton } from './SubmitButton';

interface ReauthFields {
  password: string;
}

/**
 * docs/05-ADMIN-DASHBOARD.md §6.7 — "session expiry shows a re-auth modal that
 * preserves unsaved form state rather than dumping the user at a login screen."
 *
 * That sentence is the whole design. Signing back in must not unmount anything,
 * so this is a modal over the live tree and never a redirect: the editor
 * underneath keeps its half-written article, and the queries that failed are
 * refetched once the new token is in place.
 *
 * The email is not asked for — the expired session says who this is, and asking
 * again would suggest they might answer differently.
 *
 * If the new password check fails, or they would rather start over, "sign out"
 * is the escape. It is deliberately the quieter of the two buttons: it is the
 * one that loses their work.
 */
export function ReauthModal() {
  const { t } = useTranslation();
  const { needsReauth, user, signIn, resumeSession, signOut } = useAuth();

  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReauthFields>({ defaultValues: { password: '' } });

  if (!needsReauth || user === null) {
    return null;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      const status = await signIn({ email: user.email, password: values.password });

      /*
       * An account with a second factor cannot be re-authenticated in place —
       * the challenge is a screen, and reaching it means leaving this one. Only
       * a full token can resume without losing the form underneath.
       */
      if (status !== 'authenticated') {
        await signOut();

        return;
      }

      reset();
      resumeSession();
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, ['password']);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  return (
    <Modal
      open
      // No dismiss: closing it would leave a dashboard whose every request
      // fails, which looks broken rather than expired. The way out is one of
      // the two buttons.
      dismissible={false}
      onClose={() => undefined}
      title={t('auth.reauth.title')}
      closeLabel={t('common.close')}
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <p className="text-body-sm text-secondary">
          {t('auth.reauth.body', { email: user.email })}
        </p>

        <FormError message={formError} />

        <Input
          {...register('password', { required: t('auth.validation.passwordRequired') })}
          type="password"
          autoComplete="current-password"
          /* eslint-disable-next-line jsx-a11y/no-autofocus -- the modal is
             not dismissible and holds one field; focus has to start there or
             a keyboard user has to hunt for it inside a trap. */
          autoFocus
          label={t('auth.fields.password')}
          error={errors.password?.message}
          required
        />

        <div className="flex flex-wrap items-center gap-3">
          <SubmitButton loading={isSubmitting}>{t('auth.reauth.submit')}</SubmitButton>

          <Button type="button" variant="ghost" onClick={() => void signOut()}>
            {t('auth.reauth.signOut')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
